import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { CHART_CONFIG, REPORT_COLORS } from '../../constants';
import type { PeakDayHour } from '../../../types/report';
import './PeakDayChart.css';

/**
 * Supply sources, drawn as filled areas. Every series is megawatts on one shared
 * axis — temperature is deliberately left to the prose rather than given a
 * second y-scale.
 *
 * These do not stack to demand: the wind figure is total generation, part of
 * which is exported rather than used on the island.
 */
const SERIES = [
  { key: 'importMw', name: 'Imported from New Brunswick', color: REPORT_COLORS.imported },
  { key: 'windMw', name: 'Wind generation', color: REPORT_COLORS.renewable },
  { key: 'fossilMw', name: 'Diesel generation', color: REPORT_COLORS.fossil },
] as const;

/**
 * Demand is the subject of this chart, so it is drawn as an unfilled line in
 * text ink rather than a fourth categorical colour — it is the total, not
 * another source competing with them for identity.
 */
const DEMAND_COLOR = '#e4e7eb';

/**
 * Recharts rounds an unconstrained axis well past the peak — a 402 MW maximum
 * becomes a 600 MW axis, flattening every series into the lower half. Pick a
 * top just above the data and hand it explicit ticks.
 */
function axisScale(maxMw: number): { top: number; ticks: number[] } {
  const top = Math.ceil((maxMw * 1.05) / 50) * 50;
  const step = 50 * Math.ceil(top / 50 / 4);
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] !== top) ticks.push(top);
  return { top, ticks };
}

export function PeakDayChart({ hours, peakHour }: { hours: PeakDayHour[]; peakHour: number }) {
  const data = hours.map((h) => ({ ...h, label: `${String(h.hour).padStart(2, '0')}:00` }));
  const { top, ticks } = axisScale(Math.max(...data.map((h) => h.loadMw), 0));

  return (
    <figure className="PeakDayChart">
      <figcaption className="PeakDayChart-caption">
        Megawatts through the day of the record peak. Total demand is the pale line
        at the top; wind generation falls away across the day while imports rise and
        the diesel units are brought on to meet it.
      </figcaption>
      <div className="PeakDayChart-plot">
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid stroke={CHART_CONFIG.gridStroke} vertical={false} />
            <XAxis dataKey="label" stroke={CHART_CONFIG.axisStroke} tickLine={false}
                   fontSize={12} interval={2} />
            <YAxis stroke={CHART_CONFIG.axisStroke} tickLine={false} axisLine={false}
                   fontSize={12} unit=" MW" width={70}
                   domain={[0, top]} ticks={ticks} allowDataOverflow />
            <Tooltip
              contentStyle={CHART_CONFIG.tooltipStyle}
              cursor={{ stroke: CHART_CONFIG.axisStroke }}
              formatter={(value: number, name: string) => [`${value} MW`, name]}
              labelFormatter={(l) => `${l} — ${data.find((d) => d.label === l)?.temperatureC ?? '—'} °C`}
            />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            {SERIES.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                fill={s.color}
                fillOpacity={0.16}
                isAnimationActive={false}
                dot={false}
              />
            ))}
            <Line
              type="monotone"
              dataKey="loadMw"
              name="Total demand"
              stroke={DEMAND_COLOR}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="PeakDayChart-note">
        Demand peaked in the {String(peakHour).padStart(2, '0')}:00 hour.
      </p>

      <details className="PeakDayChart-table">
        <summary>View as table</summary>
        <table>
          <thead>
            <tr>
              <th scope="col">Hour</th><th scope="col">Demand</th><th scope="col">Imported</th>
              <th scope="col">Wind</th><th scope="col">Diesel</th><th scope="col">Temp</th>
            </tr>
          </thead>
          <tbody>
            {data.map((h) => (
              <tr key={h.hour}>
                <td>{h.label}</td>
                <td>{h.loadMw} MW</td>
                <td>{h.importMw} MW</td>
                <td>{h.windMw} MW</td>
                <td>{h.fossilMw} MW</td>
                <td>{h.temperatureC === null ? '—' : `${h.temperatureC} °C`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
